import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { AuthContext } from './AuthContext';
import GSTVerificationModal from './GSTVerificationModal';
import PANVerificationModal from './PANVerificationModal';

// ─── Overstyled CSS Animations ──────────────────────────────────────────────
const overstyledCSS = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
}
@keyframes pulseGlow {
  0%, 100% { box-shadow: 0 0 15px rgba(99,102,241,0.4), 0 0 30px rgba(139,92,246,0.2); }
  50% { box-shadow: 0 0 25px rgba(99,102,241,0.6), 0 0 50px rgba(139,92,246,0.4); }
}
@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes borderGlow {
  0%, 100% { border-color: rgba(99,102,241,0.5); }
  50% { border-color: rgba(168,85,247,0.8); }
}
@keyframes fadeSlideUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes sparkle {
  0%, 100% { opacity: 0.3; transform: scale(0.8) rotate(0deg); }
  50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
}
`;

// ─── Constants ──────────────────────────────────────────────────────────────────

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Lakshadweep', 'Puducherry', 'Bhutan', 'Nepal', 'Bangladesh',
];

const ITEMS_PER_PAGE = 10;

const SALUTATION_OPTIONS = [
  { value: '', label: 'Select Salutation' },
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'M/s.', label: 'M/s.' },
  { value: 'Miss.', label: 'Miss.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' }
];

// ─── Entity Configuration ───────────────────────────────────────────────────────

const ENTITY_CONFIG = {
  Guest: {
    label: 'Guest', plural: 'Guests', icon: '👤',
    gradient: 'from-emerald-600 to-emerald-700',
    tabActive: 'border-emerald-500 text-emerald-600',
    tabActiveDark: 'border-emerald-400 text-emerald-400',
    badgeClass: 'bg-emerald-100 text-emerald-800',
    badgeClassDark: 'bg-emerald-900/30 text-emerald-400',
    apiPath: '/guests',
    searchParam: 'search',
    extractList: (res) => res.data.data || [],
    extractTotalPages: (res) => res.data.pagination?.totalPages || 1,
    extractCreated: (res) => res.data.data,
    displayName: (item) => item.guestName,
    displayPhone: (item) => item.phoneNo,
    displayId: (item) => item.id,
    displayExtra: (item) => item.station?.name ? `Station: ${item.station.name}` : '',
    formFields: [
      { name: 'guestName', label: 'Guest Name', required: true, type: 'text', colSpan: 1 },
      { name: 'phoneNo', label: 'Phone', required: true, type: 'text', colSpan: 1 },
      { name: 'secondaryPhoneNo', label: 'Secondary Phone', type: 'text', colSpan: 1 },
      { name: 'email', label: 'Email', type: 'email', colSpan: 1 },
      { name: 'station', label: 'Station', required: true, type: 'station-select', colSpan: 1 },
      { name: 'reference', label: 'Reference', required: true, type: 'text', colSpan: 1 },
      { name: 'gstNo', label: 'GST No', type: 'text', colSpan: 1 },
      { name: 'panNo', label: 'PAN No', type: 'text', colSpan: 1 },
    ],
    detailFields: [],
    gstPanConfig: { gstField: 'gstNo', panField: 'panNo', nameField: 'guestName' },
    buildPayload: (form, userId) => ({ ...form, salesperson: userId, createdBy: userId }),
  },
  Customer: {
    label: 'Customer', plural: 'Customers', icon: '🏠',
    gradient: 'from-blue-600 to-blue-700',
    tabActive: 'border-blue-500 text-blue-600',
    tabActiveDark: 'border-blue-400 text-blue-400',
    badgeClass: 'bg-blue-100 text-blue-800',
    badgeClassDark: 'bg-blue-900/30 text-blue-400',
    apiPath: '/customers',
    searchParam: 'filter',
    extractList: (res) => res.data.customers || [],
    extractTotalPages: (res) => res.data.totalPages || 1,
    extractCreated: (res) => res.data,
    displayName: (item) => item.name,
    displayPhone: (item) => item.phone,
    displayId: (item) => item.customerCode,
    displayExtra: (item) => item.station?.name ? `Station: ${item.station.name}` : '',
    formFields: [
      { name: 'salutation', label: 'Salutation', required: true, type: 'salutation-select', colSpan: 1 },
      { name: 'name', label: 'Name', required: true, type: 'text', colSpan: 1, uppercase: true, placeholder: 'Enter customer name' },
      { name: 'phone', label: 'Phone (Primary)', required: true, type: 'text', colSpan: 1, maxLength: 10, placeholder: 'Enter 10-digit mobile number', duplicateCheck: true },
      { name: 'secondaryPhone', label: 'Phone (Secondary)', type: 'text', colSpan: 1, maxLength: 10, placeholder: 'Enter alternate contact number' },
      { name: 'email', label: 'Email', type: 'email', colSpan: 1, placeholder: 'Enter email address', duplicateCheck: true },
      { name: 'station', label: 'Station', required: true, type: 'station-select', colSpan: 1 },
      { name: 'pinCode', label: 'Pin Code', type: 'text', colSpan: 1, placeholder: 'Enter pin code' },
      { name: 'siteAddress', label: 'Billing Address', type: 'textarea', colSpan: 2, placeholder: 'Enter billing address' },
      { name: 'deliveryAddress', label: 'Delivery Address', type: 'textarea', colSpan: 2, placeholder: 'Optional delivery address if different from billing address' },
      { name: 'gstNo', label: 'GST No', type: 'text', colSpan: 1 },
      { name: 'panNo', label: 'PAN No', type: 'text', colSpan: 1 },
    ],
    detailFields: [
      { label: 'Customer Code', render: (i) => i.customerCode },
      { label: 'Salutation', render: (i) => i.salutation },
      { label: 'Name', render: (i) => i.name },
      { label: 'Phone', render: (i) => i.phone },
      { label: 'Secondary Phone', render: (i) => i.secondaryPhone },
      { label: 'Email', render: (i) => i.email },
      { label: 'Station', render: (i) => i.station?.name },
      { label: 'Pin Code', render: (i) => i.pinCode },
      { label: 'GST No', render: (i) => i.gstNo },
      { label: 'PAN No', render: (i) => i.panNo },
      { label: 'Site Address', render: (i) => i.siteAddress },
      { label: 'Delivery Address', render: (i) => i.deliveryAddress },
      { label: 'Created At', render: (i) => i.createdAt ? new Date(i.createdAt).toLocaleDateString('en-IN') : '' },
    ],
    gstPanConfig: { gstField: 'gstNo', panField: 'panNo', nameField: 'name' },
    buildPayload: (form, userId) => ({ ...form, createdBy: userId }),
  },
  Dealer: {
    label: 'Dealer', plural: 'Dealers', icon: '🏪',
    gradient: 'from-purple-600 to-purple-700',
    tabActive: 'border-purple-500 text-purple-600',
    tabActiveDark: 'border-purple-400 text-purple-400',
    badgeClass: 'bg-purple-100 text-purple-800',
    badgeClassDark: 'bg-purple-900/30 text-purple-400',
    apiPath: '/dealers',
    searchParam: 'filter',
    extractList: (res) => res.data.dealers || [],
    extractTotalPages: (res, limit) => Math.ceil((res.data.totalDealers || 0) / limit) || 1,
    extractCreated: (res) => res.data,
    displayName: (item) => item.businessName,
    displayPhone: (item) => item.phone,
    displayId: (item) => item.id,
    displayExtra: (item) => item.station?.name ? `Station: ${item.station.name}` : '',
    formFields: [
      { name: 'businessName', label: 'Business Name', required: true, type: 'text', colSpan: 1 },
      { name: 'businessOwner', label: 'Business Owner', type: 'text', colSpan: 1 },
      { name: 'managerName', label: 'Manager Name', type: 'text', colSpan: 1 },
      { name: 'phone', label: 'Phone', required: true, type: 'text', colSpan: 1 },
      { name: 'secondaryPhone', label: 'Secondary Phone', type: 'text', colSpan: 1 },
      { name: 'email', label: 'Email', type: 'email', colSpan: 1 },
      { name: 'address', label: 'Address', required: true, type: 'textarea', colSpan: 2 },
      { name: 'station', label: 'Station', required: true, type: 'station-select', colSpan: 1 },
      { name: 'pinCode', label: 'Pin Code', required: true, type: 'text', colSpan: 1 },
      { name: 'gstNo', label: 'GST No', type: 'text', colSpan: 1 },
      { name: 'panNo', label: 'PAN No', type: 'text', colSpan: 1 },
    ],
    detailFields: [
      { label: 'Dealer ID', render: (i) => i.id },
      { label: 'Business Name', render: (i) => i.businessName },
      { label: 'Business Owner', render: (i) => i.businessOwner },
      { label: 'Manager', render: (i) => i.managerName },
      { label: 'Phone', render: (i) => i.phone },
      { label: 'Secondary Phone', render: (i) => i.secondaryPhone },
      { label: 'Email', render: (i) => i.email },
      { label: 'Address', render: (i) => i.address },
      { label: 'Station', render: (i) => i.station?.name },
      { label: 'Pin Code', render: (i) => i.pinCode },
      { label: 'GST No', render: (i) => i.gstNo },
      { label: 'PAN No', render: (i) => i.panNo },
      { label: 'Created At', render: (i) => i.createdAt ? new Date(i.createdAt).toLocaleDateString('en-IN') : '' },
    ],
    gstPanConfig: { gstField: 'gstNo', panField: 'panNo', nameField: 'businessName' },
    buildPayload: (form, userId) => ({ ...form, createdBy: userId }),
  },
  Reference: {
    label: 'Reference', plural: 'References', icon: '🤝',
    gradient: 'from-orange-600 to-orange-700',
    tabActive: 'border-orange-500 text-orange-600',
    tabActiveDark: 'border-orange-400 text-orange-400',
    badgeClass: 'bg-orange-100 text-orange-800',
    badgeClassDark: 'bg-orange-900/30 text-orange-400',
    apiPath: '/references',
    searchParam: 'search',
    extractList: (res) => res.data.references || [],
    extractTotalPages: (res) => res.data.totalPages || 1,
    extractCreated: (res) => res.data,
    displayName: (item) => item.name,
    displayPhone: (item) => item.phoneNumber,
    displayId: (item) => item.id,
    displayExtra: (item) => `${item.type || ''} | Class: ${item.classification || ''}`,
    formFields: [
      { name: 'name', label: 'Name', required: true, type: 'text', colSpan: 1 },
      { name: 'phoneNumber', label: 'Phone', required: true, type: 'text', colSpan: 1 },
      { name: 'secondaryPhoneNumber', label: 'Secondary Phone', type: 'text', colSpan: 1 },
      { name: 'classification', label: 'Classification', required: true, type: 'select', colSpan: 1, options: [
        { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' },
        { value: 'D', label: 'D' }, { value: 'E', label: 'E' },
      ]},
      { name: 'type', label: 'Type', required: true, type: 'select', colSpan: 1, options: [
        { value: 'Interior', label: 'Interior' }, { value: 'Designer', label: 'Designer' }, { value: 'Architect', label: 'Architect' },
      ]},
      { name: 'station', label: 'Station', required: true, type: 'station-select', colSpan: 1 },
    ],
    detailFields: [
      { label: 'Reference ID', render: (i) => i.id },
      { label: 'Name', render: (i) => i.name },
      { label: 'Phone', render: (i) => i.phoneNumber },
      { label: 'Secondary Phone', render: (i) => i.secondaryPhoneNumber },
      { label: 'Classification', render: (i) => i.classification },
      { label: 'Type', render: (i) => i.type },
      { label: 'Station', render: (i) => i.station?.name },
      { label: 'Created At', render: (i) => i.createdAt ? new Date(i.createdAt).toLocaleDateString('en-IN') : '' },
    ],
    buildPayload: (form, userId) => ({ ...form, createdBy: userId }),
  },
  MA: {
    label: 'Master Account', plural: 'Master Accounts', icon: '💼',
    gradient: 'from-teal-600 to-teal-700',
    tabActive: 'border-teal-500 text-teal-600',
    tabActiveDark: 'border-teal-400 text-teal-400',
    badgeClass: 'bg-teal-100 text-teal-800',
    badgeClassDark: 'bg-teal-900/30 text-teal-400',
    apiPath: '/mas',
    searchParam: 'filter',
    extractList: (res) => res.data.mas || [],
    extractTotalPages: (res) => res.data.totalPages || 1,
    extractCreated: (res) => res.data,
    displayName: (item) => item.name,
    displayPhone: (item) => item.phoneNumber,
    displayId: (item) => item.id,
    displayExtra: (item) => item.station?.name ? `Station: ${item.station.name}` : '',
    formFields: [
      { name: 'name', label: 'Name', required: true, type: 'text', colSpan: 1 },
      { name: 'phoneNumber', label: 'Phone', required: true, type: 'text', colSpan: 1 },
      { name: 'station', label: 'Station', required: true, type: 'station-select', colSpan: 1 },
      { name: 'gstNo', label: 'GST No', type: 'text', colSpan: 1 },
      { name: 'panNo', label: 'PAN No', type: 'text', colSpan: 1 },
    ],
    detailFields: [],
    gstPanConfig: { gstField: 'gstNo', panField: 'panNo', nameField: 'name' },
    buildPayload: (form, userId) => ({ ...form, createdBy: userId }),
  },
  Supplier: {
    label: 'Supplier', plural: 'Suppliers', icon: '📦',
    gradient: 'from-indigo-600 to-indigo-700',
    tabActive: 'border-indigo-500 text-indigo-600',
    tabActiveDark: 'border-indigo-400 text-indigo-400',
    badgeClass: 'bg-indigo-100 text-indigo-800',
    badgeClassDark: 'bg-indigo-900/30 text-indigo-400',
    apiPath: '/suppliers',
    searchParam: 'search',
    extractList: (res) => res.data.data?.suppliers || [],
    extractTotalPages: (res) => res.data.data?.totalPages || 1,
    extractCreated: (res) => res.data.supplier,
    displayName: (item) => item.name,
    displayPhone: () => null,
    displayId: (item) => item.id,
    displayExtra: (item) => `Station: ${item.station || 'N/A'} | Pin: ${item.pinCode || 'N/A'}`,
    formFields: [
      { name: 'name', label: 'Name', required: true, type: 'text', colSpan: 1 },
      { name: 'station', label: 'Station (City/Location)', required: true, type: 'text', colSpan: 1 },
      { name: 'pinCode', label: 'Pin Code', required: true, type: 'text', colSpan: 1 },
      { name: 'gstin', label: 'GSTIN', type: 'text', colSpan: 1 },
    ],
    detailFields: [
      { label: 'Supplier ID', render: (i) => i.id },
      { label: 'Name', render: (i) => i.name },
      { label: 'Station', render: (i) => i.station },
      { label: 'Pin Code', render: (i) => i.pinCode },
      { label: 'GSTIN', render: (i) => i.gstin },
      { label: 'Created At', render: (i) => i.createdAt ? new Date(i.createdAt).toLocaleDateString('en-IN') : '' },
    ],
    gstPanConfig: { gstField: 'gstin', panField: null, nameField: 'name' },
    buildPayload: (form, userId) => ({ ...form, createdBy: userId }),
  },
  Transporter: {
    label: 'Transporter', plural: 'Transporters', icon: '🚚',
    gradient: 'from-rose-600 to-rose-700',
    tabActive: 'border-rose-500 text-rose-600',
    tabActiveDark: 'border-rose-400 text-rose-400',
    badgeClass: 'bg-rose-100 text-rose-800',
    badgeClassDark: 'bg-rose-900/30 text-rose-400',
    apiPath: '/transporters',
    searchParam: 'search',
    extractList: (res) => res.data.transporters || [],
    extractTotalPages: (res, limit) => Math.ceil((res.data.total || 0) / limit) || 1,
    extractCreated: (res) => res.data,
    displayName: (item) => item.name,
    displayPhone: (item) => item.phoneNumber,
    displayId: (item) => item.id,
    displayExtra: (item) => item.gstin ? `GSTIN: ${item.gstin}` : (item.station?.name ? `Station: ${item.station.name}` : ''),
    formFields: [
      { name: 'name', label: 'Name', required: true, type: 'text', colSpan: 1 },
      { name: 'phoneNumber', label: 'Phone', required: true, type: 'text', colSpan: 1 },
      { name: 'station', label: 'Station', required: true, type: 'station-select', colSpan: 1 },
      { name: 'gstin', label: 'GSTIN', type: 'text', colSpan: 1 },
      { name: 'panNo', label: 'PAN No', type: 'text', colSpan: 1 },
    ],
    detailFields: [
      { label: 'Transporter ID', render: (i) => i.id },
      { label: 'Name', render: (i) => i.name },
      { label: 'Phone', render: (i) => i.phoneNumber },
      { label: 'Station', render: (i) => i.station?.name },
      { label: 'GSTIN', render: (i) => i.gstin },
      { label: 'PAN No', render: (i) => i.panNo },
    ],
    gstPanConfig: { gstField: 'gstin', panField: 'panNo', nameField: 'name' },
    buildPayload: (form, userId) => ({ ...form, createdBy: userId }),
  },
};

// ─── Helper: get initial form state for an entity type ──────────────────────────

const getInitialFormState = (entityType) => {
  const config = ENTITY_CONFIG[entityType];
  if (!config) return {};
  const state = {};
  config.formFields.forEach((field) => {
    if (field.type === 'select' && field.options?.length) {
      state[field.name] = field.options[0].value;
    } else {
      state[field.name] = '';
    }
  });
  return state;
};

// ─── Component ──────────────────────────────────────────────────────────────────

const MasterSelection = ({
  isOpen,
  onClose,
  onSelect,
  allowedTypes = ['Guest', 'Customer', 'Dealer', 'Reference', 'MA', 'Supplier', 'Transporter'],
  defaultType = 'Customer',
  title = 'Select Master',
}) => {
  const { user } = useContext(AuthContext);

  // Core state
  const [activeTab, setActiveTab] = useState(allowedTypes.includes(defaultType) ? defaultType : allowedTypes[0]);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // UI state
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [stations, setStations] = useState([]);

  // Create form state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  // Station sub-modal
  const [showCreateStation, setShowCreateStation] = useState(false);
  const [stationForm, setStationForm] = useState({ name: '', state: '' });
  const [isCreatingStation, setIsCreatingStation] = useState(false);

  // View mode: 'card' or 'table'
  const [viewMode, setViewMode] = useState('card');

  // Keyboard navigation
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);

  // Expanded detail items (inline)
  const [expandedItems, setExpandedItems] = useState(new Set());

  // GST/PAN verification states for create form
  const [msIsPanReadOnly, setMsIsPanReadOnly] = useState(false);
  const [msIsGstReadOnly, setMsIsGstReadOnly] = useState(false);
  const [msShowGstPanFields, setMsShowGstPanFields] = useState(false);
  const [msShowGSTVerification, setMsShowGSTVerification] = useState(false);
  const [msShowPANVerification, setMsShowPANVerification] = useState(false);

  // Duplicate checking states for create form
  const [msDuplicateErrors, setMsDuplicateErrors] = useState({});
  const [msPhoneDuplicateInfo, setMsPhoneDuplicateInfo] = useState(null);
  const [msShowPhoneDuplicateModal, setMsShowPhoneDuplicateModal] = useState(false);
  const [msDuplicatePhoneCustomers, setMsDuplicatePhoneCustomers] = useState([]);
  const [msPendingCreateSubmission, setMsPendingCreateSubmission] = useState(false);

  // ─── Keyboard Navigation ──────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      // Escape to close
      if (e.key === 'Escape') {
        if (showCreateModal) { handleCloseCreate(); return; }
        onClose();
        return;
      }
      // Don't intercept keys when typing in search or create form
      const tag = document.activeElement?.tagName?.toLowerCase();
      const isInput = tag === 'input' || tag === 'textarea' || tag === 'select';
      const isSearchInput = document.activeElement === searchInputRef.current;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(prev => {
          const next = prev < items.length - 1 ? prev + 1 : 0;
          scrollToItem(next);
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex(prev => {
          const next = prev > 0 ? prev - 1 : items.length - 1;
          scrollToItem(next);
          return next;
        });
      } else if (e.key === 'Enter' && focusedIndex >= 0 && focusedIndex < items.length && (!isInput || isSearchInput)) {
        e.preventDefault();
        handleSelect(items[focusedIndex]);
      } else if (e.key === '/' && !isInput) {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'Tab' && !isInput && !e.shiftKey) {
        e.preventDefault();
        // Cycle to next tab
        const currentIdx = allowedTypes.indexOf(activeTab);
        const nextIdx = (currentIdx + 1) % allowedTypes.length;
        handleTabChange(allowedTypes[nextIdx]);
      } else if (e.key === 'Tab' && !isInput && e.shiftKey) {
        e.preventDefault();
        // Cycle to previous tab
        const currentIdx = allowedTypes.indexOf(activeTab);
        const prevIdx = (currentIdx - 1 + allowedTypes.length) % allowedTypes.length;
        handleTabChange(allowedTypes[prevIdx]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, items, focusedIndex, showCreateModal, activeTab, allowedTypes]);

  const scrollToItem = (index) => {
    const listEl = listRef.current;
    if (!listEl) return;
    const items = listEl.querySelectorAll('[data-item-index]');
    if (items[index]) {
      items[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset focus when items change
  useEffect(() => {
    setFocusedIndex(-1);
    setExpandedItems(new Set());
  }, [items, activeTab]);

  // ─── Effects ────────────────────────────────────────────────────────────────

  // Dark mode polling
  useEffect(() => {
    const interval = setInterval(() => {
      const dm = localStorage.getItem('darkMode') === 'true';
      setIsDarkMode(dm);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Reset tab when allowedTypes or defaultType changes
  useEffect(() => {
    const tab = allowedTypes.includes(defaultType) ? defaultType : allowedTypes[0];
    setActiveTab(tab);
  }, [allowedTypes, defaultType]);

  // Fetch stations for create form dropdowns
  useEffect(() => {
    if (!isOpen) return;
    const fetchStations = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const stationsData = Array.isArray(res.data) ? res.data : (res.data.stations || res.data.data || []);
        setStations(stationsData);
      } catch (err) {
        console.error('Error fetching stations:', err);
      }
    };
    fetchStations();
  }, [isOpen]);

  // Fetch items when tab, page, or search changes
  const fetchItems = useCallback(async () => {
    if (!isOpen) return;
    const config = ENTITY_CONFIG[activeTab];
    if (!config) return;

    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        [config.searchParam]: searchTerm,
      };
      if (user?.company) {
        params.userCompanyId = user.company;
      }

      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}${config.apiPath}`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      setItems(config.extractList(res));
      setTotalPages(config.extractTotalPages(res, ITEMS_PER_PAGE));
    } catch (err) {
      console.error(`Error fetching ${activeTab}:`, err);
      setItems([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [isOpen, activeTab, currentPage, searchTerm, user]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setSearchTerm('');
    setCurrentPage(1);
    setItems([]);
    setTotalPages(1);
    setShowCreateModal(false);
    setExpandedItems(new Set());
    setFocusedIndex(-1);
    // Reset GST/PAN verification state
    setMsIsPanReadOnly(false);
    setMsIsGstReadOnly(false);
    setMsShowGstPanFields(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelect = (item) => {
    onSelect(item, activeTab);
    onClose();
  };

  const handleOpenCreate = () => {
    setCreateForm(getInitialFormState(activeTab));
    setShowCreateModal(true);
    // Reset GST/PAN verification state
    setMsIsPanReadOnly(false);
    setMsIsGstReadOnly(false);
    setMsShowGstPanFields(false);
    // Reset duplicate checking state
    setMsDuplicateErrors({});
    setMsPhoneDuplicateInfo(null);
  };

  const handleCloseCreate = () => {
    setShowCreateModal(false);
    setCreateForm({});
    // Reset GST/PAN verification state
    setMsIsPanReadOnly(false);
    setMsIsGstReadOnly(false);
    setMsShowGstPanFields(false);
    // Reset duplicate checking state
    setMsDuplicateErrors({});
    setMsPhoneDuplicateInfo(null);
  };

  const toggleItemExpand = (itemId) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleFormChange = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  // ─── GST/PAN Verification Handlers ──────────────────────────────────────────

  const handleMsGSTVerificationData = ({ gstData, gstin, useData }) => {
    if (useData && gstData) {
      const gpc = ENTITY_CONFIG[activeTab]?.gstPanConfig;
      if (!gpc) return;
      const extractedData = gstData.extractedData || {};
      const extractedPAN = gstin.substring(2, 12);

      setCreateForm(prev => {
        const updates = { ...prev, [gpc.gstField]: gstin };
        if (gpc.panField) updates[gpc.panField] = extractedPAN;
        if (gpc.nameField && (extractedData.legalName || extractedData.tradeName)) {
          updates[gpc.nameField] = extractedData.legalName || extractedData.tradeName || prev[gpc.nameField];
        }
        return updates;
      });

      setMsIsPanReadOnly(true);
      setMsIsGstReadOnly(true);
      setMsShowGstPanFields(true);

      const populatedFields = [];
      if (extractedData.legalName || extractedData.tradeName) populatedFields.push('Name');
      if (extractedPAN) populatedFields.push('PAN');

      Swal.fire({
        title: 'GST Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>GSTIN:</strong> ${gstin}</p>
            <p class="mb-2"><strong>Extracted PAN:</strong> ${extractedPAN}</p>
            <p class="mb-2"><strong>Status:</strong> ${gstData.sts || 'N/A'}</p>
            <p class="mb-2"><strong>Constitution:</strong> ${gstData.ctb || 'N/A'}</p>
            <p class="mb-2"><strong>Registration Date:</strong> ${gstData.rgdt || 'N/A'}</p>
            ${populatedFields.length > 0 ? `<p class="mt-3 text-sm text-green-600"><strong>Auto-filled:</strong> ${populatedFields.join(', ')}</p>` : ''}
            <p class="mt-3 text-sm text-blue-600">ℹ️ PAN has been automatically extracted from GSTIN.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3b82f6'
      });
    }
  };

  const handleMsPANVerificationData = ({ panNo, verificationData }) => {
    const gpc = ENTITY_CONFIG[activeTab]?.gstPanConfig;
    if (!gpc) return;

    if (gpc.panField) {
      setCreateForm(prev => ({ ...prev, [gpc.panField]: panNo }));
    }
    setMsIsPanReadOnly(true);
    setMsShowGstPanFields(true);

    const gstinList = verificationData.gstinResList || [];

    if (gstinList.length > 0) {
      const gstinOptions = gstinList.map(item =>
        `<option value="${item.gstin}">${item.gstin} (${item.authStatus || 'N/A'} - State: ${item.stateCd || 'N/A'})</option>`
      ).join('');

      Swal.fire({
        icon: 'success',
        title: 'PAN Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-3"><strong>PAN:</strong> ${panNo}</p>
            <p class="mb-2 font-semibold">This PAN has ${gstinList.length} linked GSTIN(s):</p>
            <select id="gstin-select" class="w-full border border-gray-300 rounded-md p-2 mb-3">
              <option value="">-- Select a GSTIN --</option>
              ${gstinOptions}
            </select>
            <p class="text-sm text-blue-600">💡 Select a GSTIN to auto-fill GST field</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Use Selected GSTIN',
        cancelButtonText: 'Skip',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        preConfirm: () => {
          const selectedGSTIN = document.getElementById('gstin-select').value;
          if (!selectedGSTIN) {
            Swal.showValidationMessage('Please select a GSTIN');
            return false;
          }
          return selectedGSTIN;
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          setCreateForm(prev => ({ ...prev, [gpc.gstField]: result.value }));
          setMsIsGstReadOnly(true);
          Swal.fire({
            icon: 'success',
            title: 'GSTIN Set!',
            html: `<p><strong>Selected:</strong> ${result.value}</p>`,
            timer: 2000, showConfirmButton: false
          });
        }
      });
    } else {
      Swal.fire({
        icon: 'success',
        title: 'PAN Verified!',
        html: `<p><strong>PAN:</strong> ${panNo}</p><p class="text-sm text-yellow-600 mt-2">⚠️ No linked GSTINs found.</p>`,
        timer: 2000, showConfirmButton: false
      });
    }
  };

  const handleMsClearGST = () => {
    const gpc = ENTITY_CONFIG[activeTab]?.gstPanConfig;
    if (!gpc) return;
    setCreateForm(prev => {
      const updates = { ...prev, [gpc.gstField]: '' };
      if (gpc.panField) updates[gpc.panField] = '';
      return updates;
    });
    setMsIsPanReadOnly(false);
    setMsIsGstReadOnly(false);
    setMsShowGstPanFields(false);
  };

  // ─── Duplicate Checking for Customer Create ──────────────────────────────────

  const msCheckFieldDuplicate = async (fieldName, value) => {
    if (!value?.trim()) {
      setMsDuplicateErrors(prev => ({ ...prev, [fieldName]: null }));
      if (fieldName === 'phone') setMsPhoneDuplicateInfo(null);
      return;
    }
    try {
      const checkData = {};
      checkData[fieldName] = value.trim();
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/customers/validate-unique`,
        checkData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.hasDuplicates && response.data.duplicates[fieldName]) {
        if (fieldName === 'phone') {
          setMsDuplicateErrors(prev => ({ ...prev, [fieldName]: null }));
          const count = response.data.duplicates.phone.count;
          setMsPhoneDuplicateInfo(`This phone number is used by ${count} existing customer${count > 1 ? 's' : ''}. You can still proceed.`);
        } else {
          setMsDuplicateErrors(prev => ({
            ...prev,
            [fieldName]: response.data.duplicates[fieldName].message
          }));
        }
      } else {
        setMsDuplicateErrors(prev => ({ ...prev, [fieldName]: null }));
        if (fieldName === 'phone') setMsPhoneDuplicateInfo(null);
      }
    } catch (error) {
      console.error(`Error checking ${fieldName} duplicate:`, error);
    }
  };

  const msCheckForDuplicates = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/customers/validate-unique`,
        {
          phone: createForm.phone?.trim(),
          email: createForm.email?.trim(),
          gstNo: createForm.gstNo?.trim(),
          panNo: createForm.panNo?.trim(),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return { hasDuplicates: false, duplicates: {} };
    }
  };

  // ─── Create Entity ──────────────────────────────────────────────────────────

  const handleCreateEntity = async (skipPhoneCheck = false) => {
    const config = ENTITY_CONFIG[activeTab];
    if (!config) return;

    // Validate required fields
    const missingFields = config.formFields
      .filter((f) => f.required && !createForm[f.name]?.toString().trim())
      .map((f) => f.label);

    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Required Fields',
        html: `Please fill in: <b>${missingFields.join(', ')}</b>`,
      });
      return;
    }

    // Field-level validation (matching CustomerManagement)
    if (activeTab === 'Customer') {
      if (createForm.phone && !/^\d{10}$/.test(createForm.phone)) {
        Swal.fire({ icon: 'error', title: 'Invalid Phone Number', text: 'Primary phone number must be exactly 10 digits.' });
        return;
      }
      if (createForm.secondaryPhone && !/^\d{10}$/.test(createForm.secondaryPhone)) {
        Swal.fire({ icon: 'error', title: 'Invalid Secondary Phone', text: 'Secondary phone number must be exactly 10 digits.' });
        return;
      }
      if (createForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email.trim())) {
        Swal.fire({ icon: 'error', title: 'Invalid Email', text: 'Please enter a valid email address.' });
        return;
      }
      if (createForm.gstNo && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/.test(createForm.gstNo)) {
        Swal.fire({ icon: 'error', title: 'Invalid GSTIN', text: 'Please enter a valid GSTIN number.' });
        return;
      }
      if (createForm.panNo && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(createForm.panNo)) {
        Swal.fire({ icon: 'error', title: 'Invalid PAN', text: 'Please enter a valid PAN number (e.g., ABCDE1234F).' });
        return;
      }

      // Check for duplicates before creating
      const duplicateCheck = await msCheckForDuplicates();

      // Handle phone duplicates - show confirmation modal
      if (duplicateCheck.hasDuplicates && duplicateCheck.duplicates.phone && !skipPhoneCheck) {
        setMsDuplicatePhoneCustomers(duplicateCheck.duplicates.phone.customers || []);
        setMsShowPhoneDuplicateModal(true);
        setMsPendingCreateSubmission(true);
        return;
      }

      // Handle other duplicates (email, GST, PAN) - block submission
      if (duplicateCheck.hasDuplicates) {
        const otherDuplicates = Object.entries(duplicateCheck.duplicates)
          .filter(([field]) => field !== 'phone')
          .map(([, data]) => `• ${data.message}`)
          .join('\n');

        if (otherDuplicates) {
          Swal.fire({
            icon: 'error',
            title: 'Duplicate Values Found',
            text: `The following duplicates were found:\n\n${otherDuplicates}`,
            confirmButtonColor: '#3b82f6'
          });
          return;
        }
      }
    }

    setIsCreating(true);
    try {
      // Build payload: start with form, clean empty values, add createdBy
      const rawPayload = config.buildPayload({ ...createForm }, user?.user?._id || user?._id);
      const payload = {};
      Object.entries(rawPayload).forEach(([key, val]) => {
        if (typeof val === 'string' && val.trim() === '') return;
        if (val !== undefined && val !== null) payload[key] = val;
      });

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${config.apiPath}`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      const newEntity = config.extractCreated(res);

      Swal.fire({
        icon: 'success',
        title: `${config.label} Created`,
        text: `${config.displayName(newEntity) || 'New record'} created successfully`,
        timer: 1500,
        showConfirmButton: false,
      });

      handleCloseCreate();
      handleSelect(newEntity);
    } catch (err) {
      console.error(`Error creating ${activeTab}:`, err);
      Swal.fire({
        icon: 'error',
        title: 'Creation Failed',
        text: err.response?.data?.message || err.response?.data?.error || err.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  // ─── Create Station ─────────────────────────────────────────────────────────

  const handleCreateStation = async () => {
    if (!stationForm.name.trim() || !stationForm.state) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Station name and state are required' });
      return;
    }

    setIsCreatingStation(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/stations`,
        { name: stationForm.name.toUpperCase().trim(), state: stationForm.state, createdBy: user?.user?._id || user?._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      const newStation = res.data.station || res.data.data || res.data;
      setStations((prev) => [...prev, newStation]);
      setCreateForm((prev) => ({ ...prev, station: newStation._id }));
      setShowCreateStation(false);
      setStationForm({ name: '', state: '' });

      Swal.fire({ icon: 'success', title: 'Station Created', timer: 1200, showConfirmButton: false });
    } catch (err) {
      console.error('Error creating station:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    } finally {
      setIsCreatingStation(false);
    }
  };

  // ─── react-select styles ────────────────────────────────────────────────────

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: isDarkMode ? '#374151' : 'white',
      borderColor: isDarkMode ? '#4B5563' : (state.isFocused ? '#3B82F6' : '#D1D5DB'),
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none',
      '&:hover': { borderColor: isDarkMode ? '#6B7280' : '#9CA3AF' },
      minHeight: '38px',
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 8px' }),
    input: (base) => ({ ...base, color: isDarkMode ? '#F3F4F6' : '#111827', margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: (base) => ({ ...base, height: '38px' }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDarkMode ? '#1F2937' : 'white',
      border: isDarkMode ? '1px solid #374151' : '1px solid #E5E7EB',
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? (isDarkMode ? '#2563EB' : '#3B82F6')
        : state.isFocused
          ? (isDarkMode ? '#374151' : '#EFF6FF')
          : 'transparent',
      color: state.isSelected ? 'white' : (isDarkMode ? '#F3F4F6' : '#111827'),
      cursor: 'pointer',
    }),
    placeholder: (base) => ({ ...base, color: isDarkMode ? '#6B7280' : '#9CA3AF' }),
    singleValue: (base) => ({ ...base, color: isDarkMode ? '#F3F4F6' : '#111827' }),
  };

  // ─── Render Helpers ─────────────────────────────────────────────────────────

  const stationOptions = stations.map((s) => ({ value: s._id, label: s.name }));
  const stateOptions = INDIAN_STATES.map((s) => ({ value: s, label: s }));

  // Render a single form field based on field config
  const renderFormField = (field) => {
    const baseInputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
      isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 placeholder-gray-400'
    }`;

    if (field.type === 'salutation-select') {
      const salutationSelectOptions = SALUTATION_OPTIONS.filter(o => o.value).map(o => ({ value: o.value, label: o.label }));
      return (
        <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <Select
            options={salutationSelectOptions}
            value={salutationSelectOptions.find((o) => o.value === createForm[field.name]) || null}
            onChange={(opt) => handleFormChange(field.name, opt?.value || '')}
            placeholder="Select Salutation"
            isClearable
            styles={selectStyles}
          />
        </div>
      );
    }

    if (field.type === 'station-select') {
      return (
        <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Select
                options={stationOptions}
                value={stationOptions.find((o) => o.value === createForm[field.name]) || null}
                onChange={(opt) => handleFormChange(field.name, opt?.value || '')}
                placeholder="Select station..."
                isClearable
                styles={selectStyles}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowCreateStation(true)}
              className="px-3 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 text-sm font-medium shadow-sm whitespace-nowrap"
            >
              + New
            </button>
          </div>
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={createForm[field.name] || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className={baseInputClass}
          >
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={createForm[field.name] || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            rows={2}
            className={baseInputClass}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
          />
        </div>
      );
    }

    // Default: text or email input
    const hasDuplicateError = activeTab === 'Customer' && msDuplicateErrors[field.name];
    const hasPhoneDuplicateInfo = activeTab === 'Customer' && field.name === 'phone' && msPhoneDuplicateInfo;
    const errorBorderClass = hasDuplicateError ? 'border-red-500' : (hasPhoneDuplicateInfo ? 'border-yellow-400' : '');

    return (
      <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={field.type === 'email' ? 'email' : 'text'}
          value={createForm[field.name] || ''}
          onChange={(e) => handleFormChange(field.name, field.uppercase ? e.target.value.toUpperCase() : e.target.value)}
          onBlur={field.duplicateCheck && activeTab === 'Customer' ? (e) => msCheckFieldDuplicate(field.name, e.target.value) : undefined}
          className={`${baseInputClass} ${errorBorderClass}`}
          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
          {...(field.maxLength ? { maxLength: field.maxLength } : {})}
          {...(field.uppercase ? { style: { textTransform: 'uppercase' } } : {})}
        />
        {hasPhoneDuplicateInfo && (
          <div className="flex items-start gap-1 mt-1">
            <svg className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-yellow-700 text-xs">{msPhoneDuplicateInfo}</span>
          </div>
        )}
        {hasDuplicateError && (
          <span className="text-red-500 text-xs mt-1">{msDuplicateErrors[field.name]}</span>
        )}
      </div>
    );
  };

  // ─── Render: Pagination ─────────────────────────────────────────────────────

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 text-sm rounded-xl font-medium transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 ${
            isDarkMode
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-2 border-gray-600 hover:border-indigo-500/50'
              : 'bg-white text-gray-700 hover:bg-indigo-50 border-2 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'
          }`}
        >
          ← Previous
        </button>
        <span 
          className="text-sm font-bold px-4 py-2 rounded-xl text-white"
          style={{
            background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
            boxShadow: '0 2px 10px rgba(99,102,241,0.3)',
          }}
        >
          {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className={`px-4 py-2 text-sm rounded-xl font-medium transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 ${
            isDarkMode
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-2 border-gray-600 hover:border-indigo-500/50'
              : 'bg-white text-gray-700 hover:bg-indigo-50 border-2 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'
          }`}
        >
          Next →
        </button>
      </div>
    );
  };

  // ─── Early return if not open ───────────────────────────────────────────────

  if (!isOpen) return null;

  // Inject overstyled CSS
  if (!document.getElementById('master-selection-overstyled-css')) {
    const style = document.createElement('style');
    style.id = 'master-selection-overstyled-css';
    style.textContent = overstyledCSS;
    document.head.appendChild(style);
  }

  const config = ENTITY_CONFIG[activeTab];
  const hasDetailFields = config?.detailFields?.length > 0;

  // ─── Main Render ────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose} 
      />

      {/* Main Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          className={`relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden ${
            isDarkMode ? 'bg-gray-800 border-2 border-gray-700' : 'bg-white border-2 border-gray-200'
          }`}
          style={{
            animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            boxShadow: isDarkMode 
              ? '0 0 40px rgba(99,102,241,0.3), 0 25px 50px rgba(0,0,0,0.5)'
              : '0 0 40px rgba(99,102,241,0.2), 0 25px 50px rgba(0,0,0,0.15)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="relative flex-shrink-0 px-6 py-4 flex items-center justify-between"
            style={{
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #6366F1 100%)',
            }}
          >
            
            <div className="flex items-center gap-3 z-10">
              <div 
                className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center"
                style={{ animation: 'float 3s ease-in-out infinite' }}
              >
                <span className="text-xl">{config?.icon || '📋'}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white tracking-wide" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>{title}</h2>
                <p className="text-xs text-white/70 mt-0.5">Browse and select from your records</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="z-10 p-2.5 rounded-xl bg-white/10 hover:bg-white/25 text-white/80 hover:text-white transition-all duration-300 hover:rotate-90 hover:scale-110 backdrop-blur-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className={`flex-shrink-0 border-b overflow-x-auto ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'}`}>
            <div className="flex px-4 py-2 gap-1 min-w-max">
              {allowedTypes.map((type) => {
                const ec = ENTITY_CONFIG[type];
                if (!ec) return null;
                const isActive = activeTab === type;
                return (
                  <button
                    key={type}
                    onClick={() => handleTabChange(type)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap ${
                      isActive
                        ? 'text-white shadow-lg transform scale-105'
                        : isDarkMode 
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                    style={isActive ? {
                      background: `linear-gradient(135deg, ${ec.gradient.includes('emerald') ? '#059669, #10b981' : ec.gradient.includes('blue') ? '#2563eb, #3b82f6' : ec.gradient.includes('purple') ? '#7c3aed, #a855f7' : ec.gradient.includes('orange') ? '#ea580c, #f97316' : ec.gradient.includes('teal') ? '#0d9488, #14b8a6' : ec.gradient.includes('indigo') ? '#4f46e5, #6366f1' : '#e11d48, #f43f5e'})`,
                      boxShadow: '0 4px 15px rgba(99,102,241,0.3)',
                    } : {}}
                  >
                    <span className={`text-base ${isActive ? 'animate-bounce' : ''}`} style={isActive ? { animationDuration: '2s' } : {}}>{ec.icon}</span>
                    <span>{ec.plural}</span>
                    {isActive && <span className="w-1.5 h-1.5 bg-white rounded-full ml-1 animate-pulse" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search + Create + View Toggle */}
          <div className={`flex-shrink-0 px-6 py-4 flex items-center gap-3 border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200'}`}>
            <div className="relative flex-1 group">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-xl blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
              <svg className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-300 ${isDarkMode ? 'text-gray-500 group-focus-within:text-indigo-400' : 'text-gray-400 group-focus-within:text-indigo-500'}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search ${config?.plural?.toLowerCase() || ''}... (press / to focus)`}
                value={searchTerm}
                onChange={handleSearchChange}
                className={`relative w-full pl-11 pr-4 py-2.5 border-2 rounded-xl transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700/80 border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500 focus:bg-gray-700' 
                    : 'border-gray-200 focus:border-indigo-400 hover:border-gray-300'
                } focus:ring-4 focus:ring-indigo-500/20 focus:outline-none`}
              />
            </div>
            <button
              onClick={handleOpenCreate}
              className="relative px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95 overflow-hidden group"
              style={{
                background: 'linear-gradient(135deg, #4F46E5, #7C3AED, #A855F7)',
                boxShadow: '0 4px 20px rgba(99,102,241,0.4)',
              }}
            >
              <span className="relative z-10 flex items-center gap-1">
                <span className="text-lg leading-none">+</span> Create {config?.label || ''}
              </span>
              <div className="absolute inset-0 bg-white/0 hover:bg-white/10 transition-colors duration-300" />
            </button>
            {/* View Mode Toggle */}
            <div className={`flex rounded-xl overflow-hidden border-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
              <button
                onClick={() => setViewMode('card')}
                className={`px-3 py-2 text-sm font-medium transition-all duration-200 ${viewMode === 'card' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-inner' : isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                title="Card View"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 text-sm font-medium transition-all duration-200 ${viewMode === 'table' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-inner' : isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                title="Table View"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
              </button>
            </div>
          </div>

          {/* Keyboard hint */}
          <div className={`flex-shrink-0 px-6 py-1.5 flex items-center gap-4 text-[10px] border-b ${isDarkMode ? 'border-gray-700/30 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
            <span>⬆⬇ Navigate</span>
            <span>↵ Select</span>
            <span>/ Search</span>
            <span>Tab Switch tab</span>
            <span>Esc Close</span>
          </div>

          {/* Item List */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-6 py-4" style={{ maxHeight: '500px' }}>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="relative">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mb-3" />
                  <div className="absolute inset-0 animate-ping rounded-full h-12 w-12 border-2 border-purple-400 opacity-20" />
                </div>
                <p className={`text-sm font-medium mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading {config?.plural?.toLowerCase()}...
                </p>
                <div className="w-32 h-1 mt-3 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}">
                  <div className="h-full rounded-full" style={{
                    background: 'linear-gradient(90deg, #4F46E5, #A855F7, #4F46E5)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite linear',
                    width: '100%',
                  }} />
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <span className="text-4xl mb-3">{config?.icon || '📋'}</span>
                <p className={`text-base font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  No {config?.plural?.toLowerCase() || 'items'} found
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {searchTerm ? 'Try adjusting your search' : `Create a new ${config?.label?.toLowerCase() || 'item'} to get started`}
                </p>
              </div>
            ) : viewMode === 'card' ? (
              /* ─── Card View ─── */
              <div className="space-y-2">
                {items.map((item, idx) => {
                  const isFocused = focusedIndex === idx;
                  const isExpanded = expandedItems.has(item._id);
                  return (
                    <div
                      key={item._id}
                      data-item-index={idx}
                      className={`rounded-2xl border-2 cursor-pointer group transition-all duration-200 ${
                        isFocused
                          ? isDarkMode
                            ? 'bg-indigo-900/40 border-indigo-400 ring-2 ring-indigo-500/40'
                            : 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-300/40'
                          : isDarkMode
                            ? 'bg-gray-700/40 border-gray-600/50 hover:border-indigo-400/70 hover:bg-gray-700/80'
                            : 'bg-white/80 border-gray-200 hover:border-indigo-400 hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50'
                      }`}
                      style={{
                        animation: 'fadeSlideUp 0.3s ease forwards',
                        backdropFilter: 'blur(8px)',
                      }}
                      onMouseEnter={() => setFocusedIndex(idx)}
                    >
                      {/* Main row */}
                      <div className="flex items-center justify-between p-4" onClick={() => handleSelect(item)}>
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {/* Entity icon badge */}
                          <div 
                            className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 ${
                              isDarkMode ? config?.badgeClassDark : config?.badgeClass
                            }`}
                            style={{
                              boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
                            }}
                          >
                            {config?.icon}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={`font-semibold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {config?.displayName(item) || 'N/A'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${isDarkMode ? config?.badgeClassDark : config?.badgeClass}`}>
                                {config?.displayId(item)}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              {config?.displayPhone(item) && (
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  📞 {config.displayPhone(item)}
                                </span>
                              )}
                              {config?.displayExtra(item) && (
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {config.displayExtra(item)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          {hasDetailFields && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleItemExpand(item._id); }}
                              className={`p-1.5 rounded-lg transition-all duration-200 ${
                                isExpanded
                                  ? isDarkMode ? 'bg-indigo-600/30 text-indigo-300' : 'bg-indigo-100 text-indigo-600'
                                  : isDarkMode ? 'bg-gray-600 text-gray-400 hover:bg-gray-500 hover:text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                              title={isExpanded ? 'Collapse details' : 'Expand details'}
                            >
                              <svg className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSelect(item); }}
                            className="px-4 py-1.5 text-xs font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
                            style={{
                              background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                              boxShadow: '0 2px 10px rgba(99,102,241,0.3)',
                            }}
                          >
                            Select ✨
                          </button>
                        </div>
                      </div>

                      {/* Expanded inline details */}
                      {isExpanded && hasDetailFields && (
                        <div className={`border-t mx-4 mb-3 pt-3 ${isDarkMode ? 'border-gray-600/50' : 'border-gray-200'}`}
                          style={{ animation: 'fadeSlideUp 0.2s ease forwards' }}>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                            {config.detailFields.map((field, fIdx) => {
                              const value = field.render(item);
                              if (!value && value !== 0) return null;
                              return (
                                <div key={fIdx} className="min-w-0">
                                  <div className={`text-[10px] font-medium uppercase tracking-wider mb-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {field.label}
                                  </div>
                                  <div className={`text-xs font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`} title={String(value)}>
                                    {value}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ─── Table View ─── */
              <div className="overflow-x-auto rounded-xl border-2" style={{ borderColor: isDarkMode ? 'rgba(75,85,99,0.5)' : 'rgba(229,231,235,1)' }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className={isDarkMode ? 'bg-gray-700/80' : 'bg-gray-50'}>
                      <th className={`px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name</th>
                      <th className={`px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID</th>
                      <th className={`px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Phone</th>
                      <th className={`px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Info</th>
                      {hasDetailFields && config.detailFields.slice(0, 3).filter(f => !['Name', 'Phone', config.label + ' ID', config.label + ' Code', 'Customer Code', 'Dealer ID', 'Reference ID', 'Supplier ID', 'Transporter ID'].includes(f.label)).map((field, fIdx) => (
                        <th key={fIdx} className={`px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider hidden lg:table-cell ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{field.label}</th>
                      ))}
                      <th className={`px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Action</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700/50' : 'divide-gray-100'}`}>
                    {items.map((item, idx) => {
                      const isFocused = focusedIndex === idx;
                      return (
                        <tr
                          key={item._id}
                          data-item-index={idx}
                          className={`cursor-pointer transition-colors duration-150 ${
                            isFocused
                              ? isDarkMode ? 'bg-indigo-900/30' : 'bg-indigo-50'
                              : isDarkMode ? 'hover:bg-gray-700/60' : 'hover:bg-gray-50'
                          }`}
                          onMouseEnter={() => setFocusedIndex(idx)}
                          onClick={() => handleSelect(item)}
                        >
                          <td className={`px-3 py-2.5 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-base">{config?.icon}</span>
                              <span className="truncate max-w-[200px]">{config?.displayName(item) || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? config?.badgeClassDark : config?.badgeClass}`}>
                              {config?.displayId(item)}
                            </span>
                          </td>
                          <td className={`px-3 py-2.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {config?.displayPhone(item) || '-'}
                          </td>
                          <td className={`px-3 py-2.5 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {config?.displayExtra(item) || '-'}
                          </td>
                          {hasDetailFields && config.detailFields.slice(0, 3).filter(f => !['Name', 'Phone', config.label + ' ID', config.label + ' Code', 'Customer Code', 'Dealer ID', 'Reference ID', 'Supplier ID', 'Transporter ID'].includes(f.label)).map((field, fIdx) => (
                            <td key={fIdx} className={`px-3 py-2.5 text-xs hidden lg:table-cell ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {field.render(item) || '-'}
                            </td>
                          ))}
                          <td className="px-3 py-2.5 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSelect(item); }}
                              className="px-3 py-1 text-xs font-semibold text-white rounded-lg transition-all hover:scale-105"
                              style={{ background: 'linear-gradient(135deg, #4F46E5, #7C3AED)' }}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination - Overstyled */}
          <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700/50 bg-gray-800/50' : 'border-gray-200 bg-gradient-to-r from-gray-50/80 to-indigo-50/30'}`}>
            {renderPagination()}
          </div>
        </div>
      </div>

      {/* ─── Create Modal ──────────────────────────────────────────────────── */}
      {showCreateModal && config && (
        <div className="fixed inset-0 z-[60]">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseCreate} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div
              className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden ${
                isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
              }`}
              style={{
                animation: 'scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: isDarkMode 
                  ? '0 0 30px rgba(0,0,0,0.5), 0 20px 40px rgba(0,0,0,0.3)'
                  : '0 0 30px rgba(0,0,0,0.1), 0 20px 40px rgba(0,0,0,0.15)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Create Header */}
              <div 
                className="relative flex-shrink-0 px-6 py-5 flex items-center justify-between"
                style={{
                  background: config.gradient.includes('emerald') ? 'linear-gradient(135deg, #059669, #10b981)' 
                    : config.gradient.includes('blue') ? 'linear-gradient(135deg, #2563eb, #3b82f6)' 
                    : config.gradient.includes('purple') ? 'linear-gradient(135deg, #7c3aed, #a855f7)' 
                    : config.gradient.includes('orange') ? 'linear-gradient(135deg, #ea580c, #f97316)' 
                    : config.gradient.includes('teal') ? 'linear-gradient(135deg, #0d9488, #14b8a6)' 
                    : config.gradient.includes('indigo') ? 'linear-gradient(135deg, #4f46e5, #6366f1)' 
                    : 'linear-gradient(135deg, #e11d48, #f43f5e)',
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-xl">
                    {config.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Create New {config.label}</h3>
                    <p className="text-xs text-white/70 mt-0.5">
                      {config.formFields.filter(f => f.required).length} required field{config.formFields.filter(f => f.required).length !== 1 ? 's' : ''} 
                      {' '}• Fill in the details below
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseCreate}
                  className="p-2 rounded-xl bg-white/10 hover:bg-white/25 text-white/80 hover:text-white transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Create Form */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Required Fields Section */}
                {config.formFields.filter(f => {
                  if (!config.gstPanConfig) return f.required;
                  return f.required && f.name !== config.gstPanConfig.gstField && f.name !== config.gstPanConfig.panField;
                }).length > 0 && (
                  <div className="mb-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-1.5 h-1.5 rounded-full bg-red-500`} />
                      <p className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Required Information
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {config.formFields
                        .filter(f => {
                          if (!config.gstPanConfig) return f.required;
                          return f.required && f.name !== config.gstPanConfig.gstField && f.name !== config.gstPanConfig.panField;
                        })
                        .map((field) => renderFormField(field))}
                    </div>
                  </div>
                )}

                {/* Optional Fields Section */}
                {config.formFields.filter(f => {
                  if (!config.gstPanConfig) return !f.required;
                  return !f.required && f.name !== config.gstPanConfig.gstField && f.name !== config.gstPanConfig.panField;
                }).length > 0 && (
                  <div className="mb-5">
                    <div className={`border-t mb-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-1.5 h-1.5 rounded-full ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'}`} />
                      <p className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Additional Details
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {config.formFields
                        .filter(f => {
                          if (!config.gstPanConfig) return !f.required;
                          return !f.required && f.name !== config.gstPanConfig.gstField && f.name !== config.gstPanConfig.panField;
                        })
                        .map((field) => renderFormField(field))}
                    </div>
                  </div>
                )}

                {/* GST/PAN Verification Section */}
                {config.gstPanConfig && (
                  <div className={`pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      <p className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        GST / PAN Verification
                      </p>
                      {(msIsGstReadOnly || msIsPanReadOnly) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Verified</span>
                      )}
                    </div>
                    {!msShowGstPanFields ? (
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setMsShowGSTVerification(true)}
                          className={`flex items-center justify-center gap-2 px-4 py-3.5 border-2 border-dashed rounded-xl transition-all font-medium text-sm hover:scale-[1.02] active:scale-[0.98] ${
                            isDarkMode
                              ? 'border-blue-700 text-blue-400 hover:bg-blue-900/30 hover:border-blue-500'
                              : 'border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-500'
                          }`}
                        >
                          <span className="text-lg">🔍</span> 
                          <div className="text-left">
                            <div>Verify GST</div>
                            <div className={`text-[10px] font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Auto-extracts PAN</div>
                          </div>
                        </button>
                        {config.gstPanConfig.panField && (
                          <button
                            type="button"
                            onClick={() => setMsShowPANVerification(true)}
                            className={`flex items-center justify-center gap-2 px-4 py-3.5 border-2 border-dashed rounded-xl transition-all font-medium text-sm hover:scale-[1.02] active:scale-[0.98] ${
                              isDarkMode
                                ? 'border-green-700 text-green-400 hover:bg-green-900/30 hover:border-green-500'
                                : 'border-green-300 text-green-600 hover:bg-green-50 hover:border-green-500'
                            }`}
                          >
                            <span className="text-lg">🔍</span>
                            <div className="text-left">
                              <div>Verify PAN</div>
                              <div className={`text-[10px] font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Find linked GSTINs</div>
                            </div>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        {/* GST Field */}
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {config.formFields.find(f => f.name === config.gstPanConfig.gstField)?.label || 'GSTIN'}
                            {msIsGstReadOnly && <span className="text-xs text-green-500 ml-1">✅</span>}
                          </label>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <input
                                type="text"
                                value={createForm[config.gstPanConfig.gstField] || ''}
                                onChange={(e) => handleFormChange(config.gstPanConfig.gstField, e.target.value.toUpperCase())}
                                disabled={msIsGstReadOnly}
                                maxLength="15"
                                placeholder="Enter GSTIN"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                  msIsGstReadOnly ? 'opacity-75 cursor-not-allowed' : ''
                                } ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 placeholder-gray-400'}`}
                              />
                              {createForm[config.gstPanConfig.gstField] && (
                                <button
                                  type="button"
                                  onClick={handleMsClearGST}
                                  className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs rounded-full p-0.5 transition-colors ${
                                    isDarkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-400 hover:text-red-500'
                                  }`}
                                  title="Clear"
                                >✕</button>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setMsShowGSTVerification(true)}
                              disabled={msIsGstReadOnly}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                                msIsGstReadOnly
                                  ? isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600'
                                  : isDarkMode ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-800/50' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                              }`}
                            >
                              {msIsGstReadOnly ? '✅ Verified' : '🔍 Verify'}
                            </button>
                          </div>
                        </div>

                        {/* PAN Field (if entity supports it) */}
                        {config.gstPanConfig.panField && (
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              PAN No
                              {msIsPanReadOnly && <span className="text-xs text-green-500 ml-1">✅</span>}
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={createForm[config.gstPanConfig.panField] || ''}
                                onChange={(e) => handleFormChange(config.gstPanConfig.panField, e.target.value.toUpperCase())}
                                disabled={msIsPanReadOnly}
                                maxLength="10"
                                placeholder="Enter PAN"
                                className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                  msIsPanReadOnly ? 'opacity-75 cursor-not-allowed' : ''
                                } ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 placeholder-gray-400'}`}
                              />
                              <button
                                type="button"
                                onClick={() => setMsShowPANVerification(true)}
                                disabled={msIsPanReadOnly}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                                  msIsPanReadOnly
                                    ? isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600'
                                    : isDarkMode ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-800/50' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                }`}
                              >
                                {msIsPanReadOnly ? '✅ Verified' : '🔍 Verify'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Create Actions */}
              <div className={`px-6 py-4 border-t flex items-center justify-between ${isDarkMode ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50/50'}`}>
                <p className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <span className="text-red-500">*</span> indicates required fields
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCloseCreate}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 ${
                      isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateEntity}
                    disabled={isCreating}
                    className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-2"
                    style={{
                      background: config.gradient.includes('emerald') ? 'linear-gradient(135deg, #059669, #10b981)' 
                        : config.gradient.includes('blue') ? 'linear-gradient(135deg, #2563eb, #3b82f6)' 
                        : config.gradient.includes('purple') ? 'linear-gradient(135deg, #7c3aed, #a855f7)' 
                        : config.gradient.includes('orange') ? 'linear-gradient(135deg, #ea580c, #f97316)' 
                        : config.gradient.includes('teal') ? 'linear-gradient(135deg, #0d9488, #14b8a6)' 
                        : config.gradient.includes('indigo') ? 'linear-gradient(135deg, #4f46e5, #6366f1)' 
                        : 'linear-gradient(135deg, #e11d48, #f43f5e)',
                    }}
                  >
                    {isCreating ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      <>
                        <span className="text-base">+</span> Create {config.label}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Station Create Sub-Modal ──────────────────────────────────────── */}
      {showCreateStation && (
        <div className="fixed inset-0 z-[70]">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreateStation(false)} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div
              className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Station Header */}
              <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white flex items-center justify-between">
                <h3 className="text-lg font-bold">Create New Station</h3>
                <button
                  onClick={() => setShowCreateStation(false)}
                  className="p-2 rounded-xl hover:bg-white/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Station Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={stationForm.name}
                    onChange={(e) => setStationForm((prev) => ({ ...prev, name: e.target.value.toUpperCase() }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                    }`}
                    placeholder="Enter station name..."
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    State <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={stateOptions}
                    value={stateOptions.find((o) => o.value === stationForm.state) || null}
                    onChange={(opt) => setStationForm((prev) => ({ ...prev, state: opt?.value || '' }))}
                    placeholder="Select state..."
                    isClearable
                    styles={selectStyles}
                  />
                </div>
              </div>

              <div className={`px-6 py-4 border-t flex items-center justify-end gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => setShowCreateStation(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateStation}
                  disabled={isCreatingStation}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {isCreatingStation ? 'Creating...' : 'Create Station'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Phone Duplicate Confirmation Modal ──────────────────────────── */}
      {msShowPhoneDuplicateModal && (
        <div className="fixed inset-0 z-[70]">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setMsShowPhoneDuplicateModal(false); setMsPendingCreateSubmission(false); }} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div
              className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white flex items-center justify-between">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Duplicate Phone Number
                </h3>
                <button
                  onClick={() => { setMsShowPhoneDuplicateModal(false); setMsPendingCreateSubmission(false); }}
                  className="p-2 rounded-xl hover:bg-white/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  This phone number is already associated with the following customer(s). Do you still want to proceed?
                </p>
                {msDuplicatePhoneCustomers.length > 0 && (
                  <div className={`rounded-lg border p-3 mb-4 max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    {msDuplicatePhoneCustomers.map((cust, idx) => (
                      <div key={idx} className={`flex items-center justify-between py-1.5 ${idx > 0 ? (isDarkMode ? 'border-t border-gray-600' : 'border-t border-gray-200') : ''}`}>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          {cust.name || cust.customerCode || 'Unknown'}
                        </span>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {cust.phone}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={`px-6 py-4 border-t flex items-center justify-end gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => { setMsShowPhoneDuplicateModal(false); setMsPendingCreateSubmission(false); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setMsShowPhoneDuplicateModal(false);
                    handleCreateEntity(true);
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-yellow-500 to-orange-500 shadow-md hover:shadow-lg transition-all"
                >
                  Proceed Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── GST Verification Modal ────────────────────────────────────────── */}
      <GSTVerificationModal
        isOpen={msShowGSTVerification}
        onClose={() => setMsShowGSTVerification(false)}
        onDataReceived={handleMsGSTVerificationData}
        initialGSTIN={createForm[ENTITY_CONFIG[activeTab]?.gstPanConfig?.gstField] || ''}
      />

      {/* ─── PAN Verification Modal ────────────────────────────────────────── */}
      <PANVerificationModal
        isOpen={msShowPANVerification}
        onClose={() => setMsShowPANVerification(false)}
        onDataReceived={handleMsPANVerificationData}
        initialPAN={createForm[ENTITY_CONFIG[activeTab]?.gstPanConfig?.panField] || ''}
      />
    </div>
  );
};

export default MasterSelection;
